/**
 * BumpTaskModal Component
 * US-013D Phase 3: Modal for rescheduling (bumping) production tasks
 *
 * Features:
 * - Date picker for new date
 * - Reason selector (predefined + custom)
 * - Optional notes field
 * - Impact preview panel showing downstream effects
 * - Order impact assessment with colour coding
 */

import { useState, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import {
  AlertCircle,
  AlertTriangle,
  Calendar,
  CheckCircle2,
  Clock,
  Loader2,
  MoveRight,
  Package,
  Users
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { getBumpReasons, previewBumpImpact, bumpTask } from './productionForecastService'
import OrderImpactPanel from './OrderImpactPanel'
import { parseDateString, toLocalDateString } from '@/utils/dateUtils'

// =============================================================================
// Sub-components
// =============================================================================

function DateShiftDisplay({ originalDate, newDate, daysShifted }) {
  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    const date = parseDateString(dateStr)
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    })
  }

  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-muted-foreground">{formatDate(originalDate)}</span>
      <MoveRight className="h-4 w-4 text-muted-foreground" />
      <span className="font-medium">{formatDate(newDate)}</span>
      {daysShifted !== 0 && (
        <Badge variant={daysShifted > 0 ? 'destructive' : 'secondary'} className="text-xs">
          {daysShifted > 0 ? '+' : ''}{daysShifted} day{Math.abs(daysShifted) !== 1 ? 's' : ''}
        </Badge>
      )}
    </div>
  )
}

function ImpactPreviewPanel({ impact, loading, error }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Calculating impact...</span>
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    )
  }

  if (!impact) {
    return (
      <div className="text-center py-6 text-sm text-muted-foreground">
        Select a new date to preview impact
      </div>
    )
  }

  const { original_dates, new_dates, days_shifted, warnings, capacity_conflicts } = impact

  return (
    <div className="space-y-4">
      {/* Warnings */}
      {warnings && warnings.length > 0 && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Impact Warnings</AlertTitle>
          <AlertDescription className="text-amber-700">
            <ul className="list-disc list-inside mt-1 space-y-1">
              {warnings.map((warning, idx) => (
                <li key={idx}>{warning}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Timeline Changes */}
      <div className="space-y-2">
        <h4 className="font-medium text-sm flex items-center gap-2">
          <Calendar className="h-4 w-4 text-primary" />
          Timeline Impact
        </h4>
        <div className="grid gap-2 text-sm border rounded-lg p-3 bg-muted/30">
          {original_dates.spawn_start && (
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Spawn ready:</span>
              <DateShiftDisplay
                originalDate={original_dates.spawn_start}
                newDate={new_dates.spawn_start}
                daysShifted={days_shifted}
              />
            </div>
          )}
          {original_dates.substrate_start && (
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Substrate prep:</span>
              <DateShiftDisplay
                originalDate={original_dates.substrate_start}
                newDate={new_dates.substrate_start}
                daysShifted={days_shifted}
              />
            </div>
          )}
          {original_dates.fruiting_start && (
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Move to fruiting:</span>
              <DateShiftDisplay
                originalDate={original_dates.fruiting_start}
                newDate={new_dates.fruiting_start}
                daysShifted={days_shifted}
              />
            </div>
          )}
          {original_dates.harvest && (
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Expected harvest:</span>
              <DateShiftDisplay
                originalDate={original_dates.harvest}
                newDate={new_dates.harvest}
                daysShifted={days_shifted}
              />
            </div>
          )}
        </div>
      </div>

      {/* Capacity Conflicts */}
      {capacity_conflicts && capacity_conflicts.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-medium text-sm flex items-center gap-2">
            <Package className="h-4 w-4 text-primary" />
            Capacity Conflicts
          </h4>
          <div className="space-y-1">
            {capacity_conflicts.map((conflict, idx) => (
              <Alert key={idx} variant="destructive" className="py-2">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  {conflict.conflict_description}
                </AlertDescription>
              </Alert>
            ))}
          </div>
        </div>
      )}

      {/* Success indicator if no significant impact */}
      {(!warnings || warnings.length === 0) &&
       (!capacity_conflicts || capacity_conflicts.length === 0) && (
        <Alert className="border-emerald-200 bg-emerald-50">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          <AlertDescription className="text-emerald-700">
            No significant impact detected. Safe to proceed.
          </AlertDescription>
        </Alert>
      )}
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export default function BumpTaskModal({
  open,
  onOpenChange,
  task,
  onSuccess
}) {
  // Form state
  const [newDate, setNewDate] = useState('')
  const [reason, setReason] = useState('')
  const [customReason, setCustomReason] = useState('')
  const [notes, setNotes] = useState('')

  // Data state
  const [reasons, setReasons] = useState([])
  const [impact, setImpact] = useState(null)

  // Loading states
  const [reasonsLoading, setReasonsLoading] = useState(false)
  const [impactLoading, setImpactLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // Error state
  const [error, setError] = useState(null)
  const [impactError, setImpactError] = useState(null)

  // Load reasons (non-memoized helper)
  const loadReasons = async () => {
    setReasonsLoading(true)
    try {
      const result = await getBumpReasons()
      setReasons(result.reasons || [])
    } catch (err) {
      console.error('[BumpTaskModal] Failed to load reasons:', err)
      // Set default reasons as fallback
      setReasons([
        { value: 'equipment_issue', label: 'Equipment Issue', description: 'Equipment malfunction or unavailability' },
        { value: 'material_not_ready', label: 'Material Not Ready', description: 'Required materials not available' },
        { value: 'staff_unavailable', label: 'Staff Unavailable', description: 'Required staff not available' },
        { value: 'higher_priority_task', label: 'Higher Priority Task', description: 'Bumped for a more urgent task' },
        { value: 'other', label: 'Other', description: 'Custom reason (please specify)' }
      ])
    } finally {
      setReasonsLoading(false)
    }
  }

  // Load impact preview (memoized to use in useEffect)
  const loadImpactPreview = useCallback(async () => {
    if (!task || !newDate) return

    setImpactLoading(true)
    setImpactError(null)

    try {
      const result = await previewBumpImpact(task.id, newDate)
      setImpact(result)
    } catch (err) {
      console.error('[BumpTaskModal] Impact preview failed:', err)
      setImpactError(err.message || 'Failed to calculate impact')
    } finally {
      setImpactLoading(false)
    }
  }, [task, newDate])

  // Load bump reasons on mount
  useEffect(() => {
    if (open && reasons.length === 0) {
      loadReasons()
    }
  }, [open, reasons.length])

  // Reset form when modal opens with new task
  useEffect(() => {
    if (open && task) {
      // Calculate default new date (tomorrow)
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      setNewDate(toLocalDateString(tomorrow))
      setReason('')
      setCustomReason('')
      setNotes('')
      setImpact(null)
      setError(null)
      setImpactError(null)
    }
  }, [open, task])

  // Preview impact when date changes
  useEffect(() => {
    if (task && newDate && newDate !== task.planned_date) {
      loadImpactPreview()
    }
  }, [newDate, task, loadImpactPreview])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)

    try {
      const result = await bumpTask({
        taskId: task.id,
        newDate,
        reason,
        customReason: reason === 'other' ? customReason : undefined,
        notes: notes || undefined
      })

      if (onSuccess) {
        onSuccess(result)
      }
      onOpenChange(false)
    } catch (err) {
      console.error('[BumpTaskModal] Bump failed:', err)
      setError(err.message || 'Failed to reschedule task')
    } finally {
      setSubmitting(false)
    }
  }

  const isValid = newDate && reason && (reason !== 'other' || customReason.trim())
  const hasSignificantImpact = impact?.has_significant_impact

  if (!task) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            Reschedule Task
          </DialogTitle>
          <DialogDescription>
            Bump this task to a new date. The system will calculate downstream effects.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          {/* Task Info */}
          <div className="mb-6 p-3 bg-muted/50 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <p className="font-medium">{task.description}</p>
                <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                  {task.strain_name && (
                    <Badge variant="outline">{task.strain_name}</Badge>
                  )}
                  <span>Originally: {parseDateString(task.planned_date).toLocaleDateString('en-GB', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short'
                  })}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            {/* New Date */}
            <div className="space-y-2">
              <Label htmlFor="new-date">New Date</Label>
              <Input
                id="new-date"
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                min={toLocalDateString(new Date())}
                required
              />
            </div>

            {/* Reason */}
            <div className="space-y-2">
              <Label htmlFor="reason">Reason</Label>
              <Select value={reason} onValueChange={setReason} required>
                <SelectTrigger id="reason">
                  <SelectValue placeholder="Select reason for bump" />
                </SelectTrigger>
                <SelectContent>
                  {reasonsLoading ? (
                    <SelectItem value="loading" disabled>Loading...</SelectItem>
                  ) : (
                    reasons.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {reason && reasons.find(r => r.value === reason)?.description && (
                <p className="text-xs text-muted-foreground">
                  {reasons.find(r => r.value === reason)?.description}
                </p>
              )}
            </div>

            {/* Custom Reason (when 'other' selected) */}
            {reason === 'other' && (
              <div className="space-y-2">
                <Label htmlFor="custom-reason">Specify Reason</Label>
                <Input
                  id="custom-reason"
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  placeholder="Please describe the reason..."
                  required
                />
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional context..."
                rows={2}
              />
            </div>

            {/* Impact Preview */}
            <div className="border-t pt-4">
              <h3 className="font-medium mb-3 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Impact Preview
              </h3>
              <ImpactPreviewPanel
                impact={impact}
                loading={impactLoading}
                error={impactError}
              />
            </div>

            {/* Order Impact */}
            {impact && impact.affected_orders && impact.affected_orders.length > 0 && (
              <div className="border-t pt-4">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  Affected Orders
                </h3>
                <OrderImpactPanel orders={impact.affected_orders} compact />
              </div>
            )}
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!isValid || submitting}
              className={cn(
                hasSignificantImpact && 'bg-amber-600 hover:bg-amber-700'
              )}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Rescheduling...
                </>
              ) : hasSignificantImpact ? (
                'Confirm Bump (Impact Detected)'
              ) : (
                'Confirm Bump'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
